import * as Sentry from '@sentry/react'
import {
  Environment,
  environment,
  inDevelopment,
  inE2E,
  inLocalDev,
} from './environment'
import { WrappedFirestoreError } from '@breakoutlearning/firebase-repository/types'
import type { ZodError } from 'zod'
import { inFirefox } from './browser'
import type { FirebaseRepository } from '@breakoutlearning/firebase-repository/models/FirebaseRepository'
import { setAudioSentryPort } from '@breakoutlearning/firebase-repository/util'
import { logVitePreloadError } from 'util/vitePreloadError'

function printError(e: Error) {
  let shouldPrint = true
  // A lot of errors are expected to appear in tests and make the output very noisy
  // should you need to inspect errors, you can pass PRINT_FIRESTORE
  if (process.env.NODE_ENV === 'test') shouldPrint = false
  if (process.env.PRINT_FIRESTORE === 'test') shouldPrint = true

  if (shouldPrint) console.error(e.name, e.message, e.stack)
}

let repository: FirebaseRepository | null = null

export function setRepository(repo: FirebaseRepository) {
  repository = repo
  window.addEventListener('vite:preloadError', (event) => {
    logVitePreloadError(event, repo)
  })
}

function printZodError(e: ZodError) {
  console.error(e.errors)
}

const env = environment()
// We allow sentry in dev so that we can conditionally print firestore errors
const inDev = inDevelopment() || inLocalDev()

function logAndSkip(...args: unknown[]) {
  if (repository) {
    repository.logEvent('sentry_error', { data: JSON.stringify(args) })
  }

  if (env === Environment.production) return null
  // eslint-disable-next-line no-console
  console.log(`[Sentry] <${env}>`, ...args)
  return null
}

// Records that an event is on its way to Sentry, as a GA `sentry_error_sent`
// row.
//
// Deliberately not logAndSkip. That stringifies its whole argument list into a
// single `data` parameter, and on this path the argument was the entire Sentry
// event: tens of KB once contexts are attached, and the dead-air bundle alone
// serializes a ~150-entry ring plus two snapshots. GA batches events into one
// collect request, so a couple of those push it past the request limit, the
// POST comes back 413 Content Too Large, and every event in that batch is
// lost. It bought nothing even when it fit, because GA truncates a parameter
// value at 100 characters and 26 of those went to the literal prefix.
//
// What replaces it is at most two short scalars, so this path can no longer
// grow a batch. `sentry_event_id` is the id Sentry stores for the same event,
// which makes the row a join key back to the issue. `sentry_error_type` is
// what a reader needs to decide whether following that link is worth it.
//
// The name is new. The nine logAndSkip call sites all mean "an error happened
// and we did not send it"; this one means the opposite, and they no longer
// share a parameter shape either.
function logSentToSentry(event: Sentry.Event) {
  if (repository) {
    // Last entry, not first: LinkedErrors prepends the `cause` chain, so
    // values[0] is the innermost cause and would report `TypeError` for
    // precisely the wrapped errors someone bothered to name. Messages
    // (captureMessage, e.g. 'meeting_dead_air') carry no exception at all.
    const errorType = event.exception?.values?.at(-1)?.type ?? event.message

    // Each parameter is written only when it has a value. gtag serializes an
    // undefined one as the string "undefined", which reads as data and is not.
    repository.logEvent('sentry_error_sent', {
      ...(event.event_id ? { sentry_event_id: event.event_id } : {}),
      ...(errorType ? { sentry_error_type: errorType } : {}),
    })
  }

  if (env === Environment.production) return
  // eslint-disable-next-line no-console
  console.log(`[Sentry] <${env}>`, 'Sending event to Sentry', event)
}

if (!inE2E()) {
  Sentry.init({
    dsn: 'https://0304b5a09de4f022f6c2a80b97b64c34@o4506953915564032.ingest.us.sentry.io/4507176853504000',
    environment: env,
    tracePropagationTargets: [],
    // Disable all integrations in development
    integrations: inDev
      ? []
      : [
          Sentry.browserTracingIntegration({
            // disabling this in FF as we're getting weird errors in firefox
            instrumentNavigation: inFirefox ? false : true,
          }),
          Sentry.replayIntegration(),
        ],
    // Performance Monitoring
    tracesSampleRate: inDev ? 0 : 0.2, //  Capture 20% of the transactions - disable in dev
    // Session Replay
    replaysSessionSampleRate: 1, // Capture ALL sessions as replays for debugging purposes.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // Transport chatter, dropped before it reaches the breadcrumb ring.
    //
    // That ring holds 100 entries for the whole page, and these four sources
    // fill it on their own: the GA4 collect beacon fires per event and again on
    // its google.com fallback, the Firestore Listen channel long-polls, the
    // service-worker version check hits /commit.txt on a timer, and HubSpot
    // chat polls. A meeting emits enough of all four to evict every entry
    // logged before it within a minute or two, which is how an audio issue
    // arrives with breadcrumbs that start after the failure it is about.
    //
    // Only successful ones go. A failed Firestore channel is the client losing
    // its connection, which is worth keeping next to a dead-air event; a failed
    // GA beacon is an ad blocker.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category !== 'fetch' && breadcrumb.category !== 'xhr')
        return breadcrumb

      const url = String(breadcrumb.data?.url ?? '')
      const status = Number(breadcrumb.data?.status_code ?? 0)

      // A GA4 beacon that carries a dead-air event names it in the query
      // string (`en=meeting_dead_air_scan`, `en=meeting_dead_air_heal`) along
      // with its parameters. Those beacons are the only record of what the
      // detector decided that survives into a Sentry event: the heal outcome
      // is written after the `captureMessage` it belongs to, so its own
      // breadcrumb can never appear on that event. Dropping them would remove
      // the one place an engineer reading a dead-air issue can see whether a
      // repair was attempted. GA batches several events into a request body
      // rather than the URL when they arrive together, so this is not a
      // guarantee — it keeps the ones that are readable.
      const carriesAudioEvent = url.includes('meeting_dead_air')

      const isAnalyticsBeacon =
        url.includes('/g/collect') || url.includes('api.hubspot.com')
      // status 0 is what a blocked beacon reports; there is nothing to learn
      // from either outcome, so these go whatever happened to them.
      if (isAnalyticsBeacon && !carriesAudioEvent) return null

      const isPoll =
        url.includes('firestore.googleapis.com') || url.endsWith('/commit.txt')
      if (isPoll && status >= 200 && status < 400) return null

      return breadcrumb
    },
    beforeSend(event, hint) {
      const exception = hint?.originalException
      const isError = exception instanceof Error
      const isString = typeof exception === 'string'

      // silence livekit's updatePages error, it's benign and livekit recovers from it
      if (isError && exception.message.match(/updatePages/))
        return logAndSkip('Silenced updatePages error')

      // silence LiveKit WebRTC teardown race conditions — these occur when a server
      // message (ICE candidate or SDP answer) arrives after the peer connection is
      // already closed (e.g. user left the meeting). They are unhandled rejections
      // inside livekit-client internals and are not actionable.
      // DOMException extends Error in modern browsers but not all; check both.
      const isErrorLike = isError || exception instanceof DOMException
      if (
        isErrorLike &&
        (exception.name === 'NegotiationError' ||
          exception.name === 'InvalidStateError') &&
        exception.message.includes("signalingState is 'closed'")
      )
        return logAndSkip(
          'Silenced LiveKit teardown NegotiationError/InvalidStateError'
        )

      // livekit rejects negotiation timeout as a plain string, not an Error object
      if (isString && exception === 'negotiation timed out')
        return logAndSkip('Silenced LiveKit negotiation timed out')

      // silence Object Not Found Matching Id - these are fake positives, we don't worry about them
      if (isError && exception.message.match(/Object Not Found Matching Id/))
        return logAndSkip('Silenced Object Not Found Matching Id error')

      if (isString && exception.match(/Object Not Found Matching Id/))
        return logAndSkip('Silenced Object Not Found Matching Id error')

      // WebKit's generic message for any failed dynamic import() — a dropped chunk
      // fetch on mobile Safari/WKWebView, navigation abort, or offline. Unactionable
      // noise. Arrives both as a plain string and as a TypeError object.
      if (
        (isString && exception.match(/Importing a module script failed/)) ||
        (isError && exception.message.match(/Importing a module script failed/))
      )
        return null

      // ignore failed to load resource errors for sw.js - nothing we can do about these
      if (
        isString &&
        exception.match(/sw\.js/) &&
        (exception.match(/Load failed/i) ||
          exception.match(/Failed to fetch/i) ||
          exception.match(/NetworkError/i))
      )
        return null

      // if a "Load failed" TypeError contains a Firebase Storage URL, re-classify as
      // StorageFetchError so it's tracked as a real storage issue rather than dropped as noise
      if (
        isError &&
        exception instanceof TypeError &&
        exception.message.includes('Load failed') &&
        exception.message.includes('firebasestorage.googleapis.com')
      ) {
        if (event.exception?.values) {
          for (const value of event.exception.values) {
            if (value.type === 'TypeError') value.type = 'StorageFetchError'
          }
        }
        return event
      }

      // ignore generic Safari "Load failed" TypeErrors - these are unactionable network noise
      // (user offline, navigating away, ad blockers, Safari's aggressive resource cleanup).
      // Real storage failures are caught at the source and re-thrown as StorageFetchError.
      if (
        isError &&
        exception instanceof TypeError &&
        exception.message === 'Load failed'
      )
        return logAndSkip(
          'Silenced generic Load failed TypeError (Safari network noise)'
        )

      const isWrappedFirestoreError = exception instanceof WrappedFirestoreError

      // do not send events if user is not authenticated
      if (isWrappedFirestoreError && !event.user)
        return logAndSkip('No user info in event, skipping Sentry event')

      // do not send events for handled errors
      if (isWrappedFirestoreError && exception.isHandled)
        return logAndSkip('Silenced handled Firestore error')
      if (isWrappedFirestoreError) printError(exception)
      if (isError && exception.name === 'ZodError') {
        repository?.triggerOnZodError()
        printZodError(exception as ZodError)
      }

      // do not send events in development
      if (inDev) return logAndSkip('Silenced event in development environment')

      // Only this exit is recorded. The reclassified-StorageFetchError path
      // above also returns the event and has never written a GA row; whether
      // it should is a question for the change that makes these rows visible
      // in the Admin Inspector, which needs room_id to see them at all.
      logSentToSentry(event)

      return event
    },
  })
}

// Wire the audio-observability episode sink to the initialized @sentry/react
// (v7) client. firebase-repository can't import this client directly (it
// depends on a different @sentry/core major with a separate global carrier),
// so it emits through this injected port instead. Registered unconditionally —
// captureMessage is a no-op when Sentry was not initialized (e.g. E2E).
setAudioSentryPort({
  captureMessage: ({ message, level, tags, contexts, fingerprint }) => {
    Sentry.withScope((scope) => {
      scope.setLevel(level)
      scope.setTags(tags)
      for (const [key, value] of Object.entries(contexts)) {
        scope.setContext(key, value)
      }
      scope.setFingerprint(fingerprint)
      Sentry.captureMessage(message)
    })
  },
  addBreadcrumb: (crumb) => {
    Sentry.addBreadcrumb(crumb)
  },
  flush: (timeoutMs) => Sentry.flush(timeoutMs),
})
